/**
 * PulseFit Performance View Controller
 * 
 * Manages Banister impulse-response model curves, recovery & readiness scoring,
 * weekly/monthly suffer charts, and scoped Chart.js lifecycle management.
 */

import {
  calculateBanisterTimeSeries,
  getWorkoutTimestamp,
  getWorkoutSufferScore
} from '../performance/index.js';
import {
  calculateKarvonenHRRThresholds
} from '../telemetry/index.js';
import {
  requestHealthKitSummary
} from '../integrations/healthkit.js';
import {
  store,
  setDailyRecovery,
  setTodayHealth,
  saveStateToStorage
} from '../../store.js';
import { isDateInCurrentWeek, escapeHtml } from './workouts.js';

// Scoped Chart.js Instance Registry Map
const chartRegistry = new Map();

let perfBanisterRange = '30';
let originalWorkoutsSnapshot = null;

export function getChartInstance(canvasId) {
  return chartRegistry.get(canvasId);
}

export function destroyChart(canvasId) {
  if (chartRegistry.has(canvasId)) {
    const chart = chartRegistry.get(canvasId);
    if (chart && typeof chart.destroy === 'function') {
      chart.destroy();
    }
    chartRegistry.delete(canvasId);
  }
}

export function destroyAllCharts() {
  chartRegistry.forEach((chart) => {
    if (chart && typeof chart.destroy === 'function') {
      chart.destroy();
    }
  });
  chartRegistry.clear();
}

/**
 * Creates or recreates a Chart.js instance safely on a reused canvas element.
 */
export function createSafeChart(canvasId, config) {
  if (typeof window === 'undefined' || typeof window.Chart === 'undefined') return null;
  const canvas = document.getElementById(canvasId);
  if (!canvas) return null;

  // 1. Destroy via internal registry
  destroyChart(canvasId);

  // 2. Destroy via Chart.js global lookup if attached directly to element
  if (typeof window.Chart.getChart === 'function') {
    const existing = window.Chart.getChart(canvas);
    if (existing && typeof existing.destroy === 'function') {
      existing.destroy();
    }
  }

  const ctx = canvas.getContext('2d');
  const chartInstance = new window.Chart(ctx, config);
  chartRegistry.set(canvasId, chartInstance);
  return chartInstance;
}

export function calculateAcuteRecoveryScore(dailyRec) {
  if (!dailyRec || typeof dailyRec !== 'object') return 75;
  const factors = [];

  // 1. Sleep (score or duration)
  if (dailyRec.sleepScore !== undefined && dailyRec.sleepScore !== null && !isNaN(dailyRec.sleepScore)) {
    factors.push(Math.min(100, Math.max(0, Number(dailyRec.sleepScore))));
  } else if (dailyRec.sleepHours !== undefined && dailyRec.sleepHours !== null && !isNaN(dailyRec.sleepHours)) {
    const totalHours = Number(dailyRec.sleepHours) + (dailyRec.sleepMinutes ? Number(dailyRec.sleepMinutes) / 60 : 0);
    factors.push(Math.min(100, Math.max(0, (totalHours / 8.0) * 100)));
  }

  // 2. HRV ((hrv - 30) / 50 * 100)
  if (dailyRec.hrv !== undefined && dailyRec.hrv !== null && !isNaN(dailyRec.hrv)) {
    const hrvScore = ((Number(dailyRec.hrv) - 30) / 50) * 100;
    factors.push(Math.min(100, Math.max(0, hrvScore)));
  }

  // 3. Resting HR ((80 - restHr) / 35 * 100)
  const restHr = dailyRec.restingHr ?? dailyRec.restingHR;
  if (restHr !== undefined && restHr !== null && !isNaN(restHr)) {
    const rhrScore = ((80 - Number(restHr)) / 35) * 100;
    factors.push(Math.min(100, Math.max(0, rhrScore)));
  }

  // 4. Physical feeling (1-10 -> * 10)
  if (dailyRec.physicalFeeling !== undefined && dailyRec.physicalFeeling !== null && !isNaN(dailyRec.physicalFeeling)) {
    factors.push(Math.min(100, Math.max(0, Number(dailyRec.physicalFeeling) * 10)));
  }

  // 5. Mental feeling (1-10 -> * 10)
  if (dailyRec.mentalFeeling !== undefined && dailyRec.mentalFeeling !== null && !isNaN(dailyRec.mentalFeeling)) {
    factors.push(Math.min(100, Math.max(0, Number(dailyRec.mentalFeeling) * 10)));
  }

  if (factors.length === 0) return 75;
  const sum = factors.reduce((acc, v) => acc + v, 0);
  return Math.round(sum / factors.length);
}

export function calculatePerformanceMetrics() {
  const workouts = store.workouts || [];
  const dailyRec = getDailySubjectiveRecovery();
  const restingHr = dailyRec?.restingHr ?? dailyRec?.restingHR ?? store.userProfile?.restingHr;
  let hrThresholds = store.hrThresholds || { z2Min: 125, z3Min: 141, z4Min: 156, z5Min: 170 };
  if (restingHr) {
    const maxHr = store.userProfile?.maxHr || store.currentUser?.maxHr || 190;
    const karvonen = calculateKarvonenHRRThresholds(maxHr, restingHr);
    if (karvonen) {
      hrThresholds = karvonen;
    }
  }
  return calculateBanisterTimeSeries(workouts, { todayHealth: dailyRec, hrThresholds });
}

export function getDailySubjectiveRecovery() {
  const todayStr = new Date().toISOString().split('T')[0];
  if (store.dailyRecovery && (store.dailyRecovery.date === todayStr || store.dailyRecovery.metric_date === todayStr)) {
    return store.dailyRecovery;
  }
  if (typeof localStorage === 'undefined') {
    return (store.dailyRecovery && (store.dailyRecovery.date === todayStr || store.dailyRecovery.metric_date === todayStr)) ? store.dailyRecovery : null;
  }
  try {
    const userId = (store.currentUser && store.currentUser.id) ? store.currentUser.id : 'guest';
    const stored = localStorage.getItem(`pulsefit_daily_recovery_${userId}`);
    if (!stored) {
      const legacy = localStorage.getItem('pulsefit_daily_recovery');
      if (legacy) {
        const parsedLegacy = JSON.parse(legacy);
        if (parsedLegacy.date === todayStr || parsedLegacy.metric_date === todayStr) {
          return parsedLegacy;
        }
      }
      return (store.dailyRecovery && (store.dailyRecovery.date === todayStr || store.dailyRecovery.metric_date === todayStr)) ? store.dailyRecovery : null;
    }
    const parsed = JSON.parse(stored);
    if (parsed.date === todayStr || parsed.metric_date === todayStr) {
      return parsed;
    }
  } catch (e) {}
  return (store.dailyRecovery && (store.dailyRecovery.date === todayStr || store.dailyRecovery.metric_date === todayStr)) ? store.dailyRecovery : null;
}

export async function saveDailySubjectiveRecovery(data) {
  if (!data || typeof data !== 'object') return null;
  const todayStr = new Date().toISOString().split('T')[0];
  const payload = {
    date: todayStr,
    metric_date: todayStr,
    ...data
  };
  const targetMetricDate = payload.metric_date || payload.date || todayStr;
  payload.date = targetMetricDate;
  payload.metric_date = targetMetricDate;

  // Calculate readiness score if not provided on payload
  if (payload.readinessScore === undefined || payload.readinessScore === null) {
    try {
      const metrics = calculatePerformanceMetrics();
      const tsbNorm = Math.min(100, Math.max(0, ((metrics.form + 25) / 50) * 100));
      const acuteRec = calculateAcuteRecoveryScore(payload);
      payload.readinessScore = Math.round(0.4 * tsbNorm + 0.6 * acuteRec);
    } catch (_) {
      payload.readinessScore = null;
    }
  }

  // 1. Sync store state
  setDailyRecovery(payload);
  if (payload.restingHr !== undefined || payload.restingHR !== undefined || payload.hrv !== undefined || payload.sleepHours !== undefined || payload.sleepScore !== undefined) {
    const rhrVal = payload.restingHr ?? payload.restingHR;
    setTodayHealth({
      restingHr: rhrVal,
      restingHR: rhrVal,
      hrv: payload.hrv,
      sleepHours: payload.sleepHours,
      sleepMinutes: payload.sleepMinutes,
      sleepScore: payload.sleepScore,
      date: targetMetricDate
    });
  }

  // 2. Persist to localStorage
  if (typeof localStorage !== 'undefined') {
    try {
      const userId = (store.currentUser && store.currentUser.id) ? store.currentUser.id : 'guest';
      localStorage.setItem(`pulsefit_daily_recovery_${userId}`, JSON.stringify(payload));
    } catch (e) {
      console.warn("Failed to persist daily subjective recovery to localStorage:", e);
    }
  }

  // 3. Dual-write to Supabase daily_recovery if authenticated & not guest
  const isGuest = store.isGuest || !store.currentUser?.id || store.currentUser.id === 'guest-preview-user';
  const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;
  if (!isGuest && supabase && store.currentUser?.id) {
    try {
      const restHr = payload.restingHr ?? payload.restingHR;
      const totalSleepHours = payload.sleepHours !== undefined && payload.sleepHours !== null
        ? Number(payload.sleepHours) + (payload.sleepMinutes ? Number(payload.sleepMinutes) / 60 : 0)
        : null;

      const cloudRow = {
        user_id: store.currentUser.id,
        metric_date: targetMetricDate,
        resting_hr: restHr !== undefined && restHr !== null ? Math.round(Number(restHr)) : null,
        hrv: payload.hrv !== undefined && payload.hrv !== null ? Math.round(Number(payload.hrv)) : null,
        sleep_hours: totalSleepHours !== null ? Number(totalSleepHours.toFixed(2)) : null,
        sleep_score: payload.sleepScore !== undefined && payload.sleepScore !== null ? Math.round(Number(payload.sleepScore)) : null,
        physical_feeling: payload.physicalFeeling ? Number(payload.physicalFeeling) : null,
        mental_feeling: payload.mentalFeeling ? Number(payload.mentalFeeling) : null,
        readiness_score: payload.readinessScore,
        source: payload.source || (payload.physicalFeeling || payload.mentalFeeling ? (payload.hrv || restHr ? 'hybrid' : 'manual') : 'healthkit'),
        updated_at: new Date().toISOString()
      };

      const query = supabase.from('daily_recovery').upsert(cloudRow, { onConflict: 'user_id,metric_date' });
      if (typeof query.eq === 'function') {
        query.eq('user_id', store.currentUser.id);
      }
      await query;
    } catch (err) {
      console.warn('Failed to upsert daily recovery to Supabase:', err);
    }
  }
  return payload;
}

export function renderPerformanceView() {
  if (typeof document === 'undefined') return;

  // Request fresh HealthKit telemetry if running inside native bridge
  requestHealthKitSummary();

  const metrics = calculatePerformanceMetrics();
  const sufferGoal = (store.goals && store.goals.suffer) || 550;
  const weeklySuffer = metrics.weeklySuffer || 0;

  // 1. Update KPI Cards
  const fitnessEl = document.getElementById('kpi-fitness-val');
  if (fitnessEl) fitnessEl.innerText = escapeHtml(String(metrics.fitness));

  const fatigueEl = document.getElementById('kpi-fatigue-val');
  if (fatigueEl) fatigueEl.innerText = escapeHtml(String(metrics.fatigue));

  const formEl = document.getElementById('kpi-form-val');
  if (formEl) {
    const prefix = metrics.form >= 0 ? '+' : '';
    formEl.innerText = `${prefix}${escapeHtml(metrics.form.toFixed(1))}`;
  }

  const formBadge = document.getElementById('kpi-form-badge');
  if (formBadge) {
    if (metrics.form > 10) {
      formBadge.innerText = 'Fresh';
      formBadge.style.background = '#10b981';
    } else if (metrics.form >= -10) {
      formBadge.innerText = 'Optimal';
      formBadge.style.background = '#0ea5e9';
    } else if (metrics.form >= -25) {
      formBadge.innerText = 'Overload';
      formBadge.style.background = '#f59e0b';
    } else {
      formBadge.innerText = 'Fatigued';
      formBadge.style.background = '#ef4444';
    }
  }

  const sufferValEl = document.getElementById('kpi-suffer-val');
  if (sufferValEl) sufferValEl.innerText = escapeHtml(String(weeklySuffer));

  const sufferGoalEl = document.getElementById('kpi-suffer-goal');
  if (sufferGoalEl) sufferGoalEl.innerText = escapeHtml(String(sufferGoal));

  const sufferBarEl = document.getElementById('kpi-suffer-bar');
  if (sufferBarEl) {
    const pct = Math.min(100, Math.round((weeklySuffer / sufferGoal) * 100));
    sufferBarEl.style.width = `${pct}%`;
  }

  // 2. Daily Workout Readiness & Aggressiveness Panel
  const tsbNorm = Math.min(100, Math.max(0, ((metrics.form + 25) / 50) * 100));
  const dailyRec = getDailySubjectiveRecovery();
  const recVal = calculateAcuteRecoveryScore(dailyRec);

  const readinessScore = Math.round(0.4 * tsbNorm + 0.6 * recVal);

  const scoreEl = document.getElementById('lbl-perf-readiness-score');
  if (scoreEl) scoreEl.innerText = `${escapeHtml(String(readinessScore))}%`;

  const statusEl = document.getElementById('lbl-perf-readiness-status');
  if (statusEl) {
    if (readinessScore >= 80) statusEl.innerText = 'Peak Readiness';
    else if (readinessScore >= 60) statusEl.innerText = 'High Readiness';
    else if (readinessScore >= 40) statusEl.innerText = 'Moderate Readiness';
    else statusEl.innerText = 'Sub-Optimal Recovery';
  }

  // Update Provenance Badge
  const badgeEl = document.getElementById('lbl-perf-readiness-source-badge');
  if (badgeEl) {
    const isGuest = store.isGuest || !store.currentUser?.id || store.currentUser.id === 'guest-preview-user';
    let badgeText = '';
    let badgeBg = '';
    let badgeBorder = '';
    let badgeColor = '';

    if (dailyRec?.source === 'hybrid' || (dailyRec && (dailyRec.physicalFeeling || dailyRec.mentalFeeling) && (dailyRec.restingHr || dailyRec.hrv || dailyRec.sleepScore))) {
      badgeText = '⚡ Hybrid';
      badgeBg = 'rgba(168, 85, 247, 0.10)';
      badgeBorder = '1px solid rgba(168, 85, 247, 0.30)';
      badgeColor = '#c084fc';
    } else if (dailyRec?.source === 'healthkit' || (!dailyRec?.source && store.todayHealth && (store.todayHealth.restingHr || store.todayHealth.sleepScore))) {
      badgeText = '🍏 HealthKit Synced';
      badgeBg = 'rgba(16, 185, 129, 0.10)';
      badgeBorder = '1px solid rgba(16, 185, 129, 0.30)';
      badgeColor = '#34d399';
    } else if (dailyRec?.source === 'manual' || (dailyRec && (dailyRec.restingHr || dailyRec.sleepHours))) {
      badgeText = '✏️ Manual Check-In';
      badgeBg = 'rgba(99, 102, 241, 0.10)';
      badgeBorder = '1px solid rgba(99, 102, 241, 0.30)';
      badgeColor = '#a5b4fc';
    } else if (isGuest) {
      badgeText = '👤 Local Guest';
      badgeBg = 'rgba(255, 255, 255, 0.05)';
      badgeBorder = '1px solid rgba(255, 255, 255, 0.15)';
      badgeColor = 'var(--text-muted)';
    } else {
      badgeText = '⏳ Waiting for Telemetry';
      badgeBg = 'rgba(245, 158, 11, 0.10)';
      badgeBorder = '1px solid rgba(245, 158, 11, 0.30)';
      badgeColor = '#fbbf24';
    }

    badgeEl.innerText = badgeText;
    badgeEl.style.background = badgeBg;
    badgeEl.style.border = badgeBorder;
    badgeEl.style.color = badgeColor;
    badgeEl.style.display = 'inline-flex';
  }

  const gaugeEl = document.getElementById('perf-readiness-gauge');
  if (gaugeEl) {
    const strokeDash = 314.16;
    const offset = strokeDash * (1 - readinessScore / 100);
    gaugeEl.style.strokeDashoffset = offset;
    gaugeEl.style.stroke = readinessScore >= 80 ? '#10b981' : (readinessScore >= 60 ? '#0ea5e9' : (readinessScore >= 40 ? '#f59e0b' : '#ef4444'));
  }

  const ofiEl = document.getElementById('lbl-perf-ofi-val');
  if (ofiEl) ofiEl.innerText = `${escapeHtml(String(metrics.fitness))} / 100`;

  const rhrEl = document.getElementById('lbl-perf-rhr-val');
  const rhrVal = (dailyRec && (dailyRec.restingHr !== undefined || dailyRec.restingHR !== undefined))
    ? (dailyRec.restingHr ?? dailyRec.restingHR)
    : 58;
  if (rhrEl) rhrEl.innerText = `${escapeHtml(String(rhrVal))} bpm`;

  const sleepEl = document.getElementById('lbl-perf-sleep-val');
  const sleepVal = (dailyRec && dailyRec.sleepHours !== undefined && dailyRec.sleepHours !== null)
    ? (Number(dailyRec.sleepHours) + (dailyRec.sleepMinutes ? Number(dailyRec.sleepMinutes) / 60 : 0)).toFixed(1)
    : '7.5';
  if (sleepEl) sleepEl.innerText = `${escapeHtml(String(sleepVal))} hrs`;

  const aisEl = document.getElementById('lbl-perf-ais-val');
  if (aisEl) aisEl.innerText = `${escapeHtml(String(metrics.fatigue))} pts`;

  const reasonsList = document.getElementById('list-perf-readiness-reasons');
  if (reasonsList) {
    let reasons = [];
    if (metrics.form > 10) {
      reasons.push(`TSB (Form) is strongly positive (+${metrics.form.toFixed(1)}), signaling prime neuromuscular readiness.`);
    } else if (metrics.form < -10) {
      reasons.push(`Acute fatigue (ATL ${metrics.fatigue}) exceeds chronic fitness (CTL ${metrics.fitness}). Systemic recovery recommended.`);
    } else {
      reasons.push(`Training stress is balanced (TSB ${metrics.form >= 0 ? '+' : ''}${metrics.form.toFixed(1)}). Optimal zone for progressive aerobic build.`);
    }
    if (recVal >= 75) {
      reasons.push(`Sleep & parasympathetic recovery scores are high (${recVal}%), supporting high-intensity station efforts.`);
    } else {
      reasons.push(`Sleep score (${recVal}%) is modest. Prioritize hydration and active recovery mobility.`);
    }
    reasonsList.innerHTML = reasons.map(r => `<li>${escapeHtml(r)}</li>`).join('');
  }

  // 3. Update AI Coaching Box
  const coachingBox = document.getElementById('perf-coaching-box');
  const coachingTitle = document.getElementById('perf-coaching-title');
  const coachingText = document.getElementById('perf-coaching-text');
  if (metrics.form > 10) {
    if (coachingBox) coachingBox.style.borderColor = '#10b981';
    if (coachingTitle) { coachingTitle.innerText = 'Peak Readiness'; coachingTitle.style.color = '#10b981'; }
    if (coachingText) coachingText.innerText = 'System is fully recovered. Muscle fiber recruitment and cardiac output are prime. Target competitive efforts or PR testing.';
  } else if (metrics.form < -10) {
    if (coachingBox) coachingBox.style.borderColor = '#ef4444';
    if (coachingTitle) { coachingTitle.innerText = 'System Fatigue'; coachingTitle.style.color = '#ef4444'; }
    if (coachingText) coachingText.innerText = 'Elevated risk of injury. Systemic recovery is compromised. Advise lower volumes, active mobility work, and sleep prioritization.';
  } else {
    if (coachingBox) coachingBox.style.borderColor = '#f59e0b';
    if (coachingTitle) { coachingTitle.innerText = 'Improving & Adapting'; coachingTitle.style.color = '#f59e0b'; }
    if (coachingText) coachingText.innerText = 'Physiological adaptations are progressing cleanly. Safe zone for continued training blocks and standard aerobic progression.';
  }

  // 4. Render Chart.js Charts safely
  renderBanisterChart(metrics.historicalTimeSeries);
  renderWeeklySufferChart(sufferGoal);
  renderMonthlySufferChart(sufferGoal);

  // 5. Render Recent Workouts List
  renderPerformanceWorkoutsList();

  // Bind range tab buttons for Banister Chart
  const rangeBtns = document.querySelectorAll('#view-performance .btn-tab[data-range]');
  rangeBtns.forEach(btn => {
    btn.onclick = () => {
      rangeBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      perfBanisterRange = btn.getAttribute('data-range') || '30';
      renderBanisterChart(metrics.historicalTimeSeries);
    };
  });
}

export function renderBanisterChart(timeSeries) {
  let series = [...(timeSeries || [])];
  if (perfBanisterRange === '30') {
    series = series.slice(-30);
  } else if (perfBanisterRange === '90') {
    series = series.slice(-90);
  }

  const labels = series.map(s => s.dateLabel || s.dateStr);
  const ctlData = series.map(s => s.ctl);
  const atlData = series.map(s => s.atl);
  const tsbData = series.map(s => s.tsb);

  createSafeChart('chart-fitness-freshness', {
    type: 'line',
    data: {
      labels: labels,
      datasets: [
        {
          label: 'Fitness (CTL)',
          data: ctlData,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.08)',
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: series.length > 40 ? 0 : 2
        },
        {
          label: 'Fatigue (ATL)',
          data: atlData,
          borderColor: '#ef4444',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: series.length > 40 ? 0 : 2
        },
        {
          label: 'Form (TSB)',
          data: tsbData,
          borderColor: '#6366f1',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: series.length > 40 ? 0 : 2
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          titleColor: '#f8fafc',
          bodyColor: '#e2e8f0',
          borderColor: 'rgba(255,255,255,0.1)',
          borderWidth: 1
        }
      },
      scales: {
        x: {
          grid: { color: 'rgba(255, 255, 255, 0.04)' },
          ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 10 } }
        },
        y: {
          grid: { color: 'rgba(255, 255, 255, 0.04)' },
          ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 10 } }
        }
      }
    }
  });
}

export function renderWeeklySufferChart(sufferGoal) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dailyValues = [0, 0, 0, 0, 0, 0, 0];

  (store.workouts || []).forEach(w => {
    const ts = getWorkoutTimestamp(w);
    if (!ts || w.excludeFromStats) return;
    const d = new Date(ts);
    const dateStr = d.toLocaleDateString('sv');
    if (isDateInCurrentWeek(dateStr)) {
      const dayIdx = (d.getDay() + 6) % 7;
      dailyValues[dayIdx] += getWorkoutSufferScore(w);
    }
  });

  const dailyGoal = Math.round((sufferGoal || 550) / 7);
  const goalLine = Array(7).fill(dailyGoal);

  createSafeChart('chart-weekly-suffer', {
    type: 'bar',
    data: {
      labels: days,
      datasets: [
        {
          label: 'Suffer Score',
          data: dailyValues,
          backgroundColor: '#f97316',
          borderRadius: 6
        },
        {
          label: 'Daily Goal',
          data: goalLine,
          type: 'line',
          borderColor: 'rgba(255, 255, 255, 0.4)',
          borderDash: [4, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          fill: false
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { backgroundColor: 'rgba(15, 23, 42, 0.9)' }
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 10 } } },
        y: { grid: { color: 'rgba(255, 255, 255, 0.04)' }, ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 10 } } }
      }
    }
  });
}

export function renderMonthlySufferChart(sufferGoal) {
  const weekTotals = [0, 0, 0, 0];
  const weekLabels = ['3 Wks Ago', '2 Wks Ago', 'Last Week', 'This Week'];

  const now = new Date();
  (store.workouts || []).forEach(w => {
    const ts = getWorkoutTimestamp(w);
    if (!ts || w.excludeFromStats) return;
    const diffDays = Math.floor((now.getTime() - ts) / (1000 * 60 * 60 * 24));
    if (diffDays >= 0 && diffDays < 28) {
      const weekIdx = 3 - Math.floor(diffDays / 7);
      if (weekIdx >= 0 && weekIdx < 4) {
        weekTotals[weekIdx] += getWorkoutSufferScore(w);
      }
    }
  });

  const goalLine = Array(4).fill(sufferGoal || 550);

  createSafeChart('chart-monthly-suffer', {
    type: 'bar',
    data: {
      labels: weekLabels,
      datasets: [
        {
          label: 'Weekly Suffer',
          data: weekTotals,
          backgroundColor: '#a855f7',
          borderRadius: 6
        },
        {
          label: 'Weekly Goal',
          data: goalLine,
          type: 'line',
          borderColor: '#0ea5e9',
          borderDash: [4, 4],
          borderWidth: 2,
          pointRadius: 0,
          fill: false
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { backgroundColor: 'rgba(15, 23, 42, 0.9)' }
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 10 } } },
        y: { grid: { color: 'rgba(255, 255, 255, 0.04)' }, ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 10 } } }
      }
    }
  });
}

export function drawFormSparkline(formValues) {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('perf-form-sparkline-container');
  if (!container) return;
  
  const width = 100;
  const height = 35;
  const padding = 5;
  
  if (!formValues || formValues.length < 2) {
    container.innerHTML = '';
    return;
  }
  
  const minVal = Math.min(...formValues);
  const maxVal = Math.max(...formValues);
  const span = maxVal - minVal === 0 ? 1 : maxVal - minVal;
  
  const points = formValues.map((val, idx) => {
    const x = padding + (idx / (formValues.length - 1)) * (width - 2 * padding);
    const y = padding + (1 - (val - minVal) / span) * (height - 2 * padding);
    return { x, y };
  });
  
  let pathD = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    pathD += ` L ${points[i].x} ${points[i].y}`;
  }
  
  const currentForm = formValues[formValues.length - 1];
  let strokeColor = '#f59e0b';
  if (currentForm > 10) strokeColor = '#10b981';
  else if (currentForm < -10) strokeColor = '#ef4444';
  
  const lastPoint = points[points.length - 1];
  
  container.innerHTML = `
    <svg width="${width}" height="${height}" style="overflow: visible;">
      <path d="${pathD}" fill="none" stroke="${strokeColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 0 3px ${strokeColor}40);" />
      <circle cx="${lastPoint.x}" cy="${lastPoint.y}" r="3" fill="${strokeColor}" style="filter: drop-shadow(0 0 4px ${strokeColor});" />
    </svg>
  `;
}

export function renderPerformanceTrends(loadData, recoveryData) {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('perf-trends-chart');
  if (!container) return;
  
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const maxVal = Math.max(...loadData, ...recoveryData, 1);
  
  let html = '';
  for (let i = 0; i < 7; i++) {
    const load = loadData[i];
    const rec = recoveryData[i];
    
    const loadPct = (load / maxVal) * 100;
    const recPct = (rec / maxVal) * 100;
    
    const loadH = Math.max((loadPct / 100) * 85, 4);
    const recH = Math.max((recPct / 100) * 85, 4);
    
    html += `
      <div style="display: flex; flex-direction: column; align-items: center; gap: 8px; height: 100%; justify-content: flex-end; flex-grow: 1;">
        <div style="display: flex; align-items: flex-end; gap: 4px; height: 85px;">
          <div style="width: 8px; height: ${loadH}px; background: #f97316; border-radius: 4px 4px 0 0; transition: height 0.3s ease;" title="Load: ${load}"></div>
          <div style="width: 8px; height: ${recH}px; background: #ffffff; border-radius: 4px 4px 0 0; transition: height 0.3s ease;" title="Recovery: ${rec}"></div>
        </div>
        <span style="font-size: 10px; color: var(--text-muted); font-weight: 600; margin-top: 4px;">${days[i]}</span>
      </div>
    `;
  }
  container.innerHTML = html;
}

export function renderPerformanceWorkoutsList() {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('perf-workouts-list');
  if (!container) return;
  
  if (!store.workouts || store.workouts.length === 0) {
    container.innerHTML = `<div class="glass" style="padding: 16px; text-align: center; color: var(--text-muted); font-size: 12px;">No workouts logged yet.</div>`;
    return;
  }
  
  const sorted = [...store.workouts]
    .filter(w => !w.excludeFromStats)
    .sort((a, b) => getWorkoutTimestamp(b) - getWorkoutTimestamp(a))
    .slice(0, 10);
  
  if (sorted.length === 0) {
    container.innerHTML = `<div class="glass" style="padding: 16px; text-align: center; color: var(--text-muted); font-size: 12px;">All recent workouts are excluded from statistics.</div>`;
    return;
  }
  
  container.innerHTML = sorted.map(w => {
    const ts = getWorkoutTimestamp(w);
    const dateLabel = ts ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : (w.date || 'Recent');
    const sufferScore = getWorkoutSufferScore(w);
    const effortColor = sufferScore > 80 ? '#ef4444' : (sufferScore > 50 ? '#f59e0b' : '#10b981');
    
    return `
      <div class="glass" style="padding: 10px 14px; border-radius: 8px; display: flex; align-items: center; justify-content: space-between; border-left: 3px solid ${effortColor};">
        <div>
          <span style="font-size: 13px; font-weight: 600; color: var(--text-primary); display: block;">${escapeHtml(w.name || w.type || 'Workout')}</span>
          <span style="font-size: 11px; color: var(--text-muted);">${dateLabel} · ${w.duration || 0}m</span>
        </div>
        <div style="text-align: right;">
          <span style="font-size: 14px; font-weight: 700; color: ${effortColor};">${sufferScore}</span>
          <span style="font-size: 10px; color: var(--text-muted); display: block;">suffer</span>
        </div>
      </div>
    `;
  }).join('');
}

export function injectMockScenario(scenario) {
  if (!originalWorkoutsSnapshot) {
    originalWorkoutsSnapshot = JSON.parse(JSON.stringify(store.workouts || []));
  }
  
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  let simulatedWorkouts = [];

  if (scenario === 'overtraining') {
    // Generate 14 days of high load workouts
    for (let i = 14; i >= 0; i--) {
      simulatedWorkouts.push({
        id: `sim-ot-${i}`,
        timestamp: now - (i * dayMs),
        date: new Date(now - (i * dayMs)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        type: 'Hyrox Workout',
        duration: 90,
        calories: 950,
        avgHeartrate: 165,
        sufferScore: 115,
        sufferMultiplier: 1
      });
    }
  } else if (scenario === 'taper') {
    // Generate heavy past, but light last 5 days
    for (let i = 21; i > 5; i--) {
      simulatedWorkouts.push({
        id: `sim-tp-heavy-${i}`,
        timestamp: now - (i * dayMs),
        date: new Date(now - (i * dayMs)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        type: 'Hyrox Workout',
        duration: 75,
        calories: 800,
        avgHeartrate: 160,
        sufferScore: 90
      });
    }
    for (let i = 5; i >= 0; i--) {
      simulatedWorkouts.push({
        id: `sim-tp-light-${i}`,
        timestamp: now - (i * dayMs),
        date: new Date(now - (i * dayMs)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        type: 'Recovery Walk',
        duration: 25,
        calories: 180,
        avgHeartrate: 115,
        sufferScore: 20
      });
    }
  }

  store.workouts = simulatedWorkouts;
  renderPerformanceView();
}

export function restoreOriginalWorkouts() {
  if (originalWorkoutsSnapshot) {
    store.workouts = JSON.parse(JSON.stringify(originalWorkoutsSnapshot));
    originalWorkoutsSnapshot = null;
    renderPerformanceView();
  }
}

let isSavingRecovery = false;

export function setupPerformanceStatusModal() {
  if (typeof document === 'undefined') return;
  const dialog = document.getElementById('perf-status-dialog');
  const closeBtn = document.getElementById('close-perf-status-btn');
  const cancelBtn = document.getElementById('cancel-perf-status-btn');
  const saveBtn = document.getElementById('save-perf-status-btn');
  const updateStatusBtn = document.getElementById('btn-perf-update-status');
  if (!dialog) return;

  function closeModal() {
    if (typeof dialog.close === 'function') dialog.close();
    dialog.style.display = 'none';
    if (dialog._focusAfterClosed && typeof dialog._focusAfterClosed.focus === 'function') {
      dialog._focusAfterClosed.focus();
      dialog._focusAfterClosed = null;
    }
  }

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (cancelBtn) cancelBtn.addEventListener('click', closeModal);

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) {
      closeModal();
    }
  });

  dialog.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
    }
  });

  function updatePerfSubjectiveLabels() {
    const physContainer = document.getElementById('perf-phys-rating-container');
    const physDisplay = document.getElementById('perf-physical-feeling-display');
    if (physContainer && physDisplay) {
      const activeBtn = physContainer.querySelector('.rating-pill-btn.active');
      if (activeBtn) {
        physDisplay.innerText = `${activeBtn.getAttribute('data-value')} / 10`;
      } else {
        physDisplay.innerText = 'Select...';
      }
    }

    const mentContainer = document.getElementById('perf-ment-rating-container');
    const mentDisplay = document.getElementById('perf-mental-feeling-display');
    if (mentContainer && mentDisplay) {
      const activeBtn = mentContainer.querySelector('.rating-pill-btn.active');
      if (activeBtn) {
        mentDisplay.innerText = `${activeBtn.getAttribute('data-value')} / 10`;
      } else {
        mentDisplay.innerText = 'Select...';
      }
    }

    const restingHrSlider = document.getElementById('perf-subjective-resting-hr');
    const restingHrDisplay = document.getElementById('perf-resting-hr-display');
    if (restingHrSlider && restingHrDisplay) {
      restingHrDisplay.innerText = `${restingHrSlider.value} bpm`;
      restingHrSlider.setAttribute('aria-valuenow', restingHrSlider.value);
      restingHrSlider.setAttribute('aria-valuetext', `${restingHrSlider.value} beats per minute`);
    }

    const hrvSlider = document.getElementById('perf-subjective-hrv');
    const hrvDisplay = document.getElementById('perf-hrv-display');
    if (hrvSlider && hrvDisplay) {
      hrvDisplay.innerText = `${hrvSlider.value} ms`;
      hrvSlider.setAttribute('aria-valuenow', hrvSlider.value);
      hrvSlider.setAttribute('aria-valuetext', `${hrvSlider.value} milliseconds`);
    }

    const sleepSlider = document.getElementById('perf-subjective-sleep');
    const sleepDisplay = document.getElementById('perf-sleep-display');
    if (sleepSlider && sleepDisplay) {
      const val = parseFloat(sleepSlider.value);
      const h = Math.floor(val);
      const m = Math.round((val - h) * 60);
      sleepDisplay.innerText = `${h}h ${m}m`;
      sleepSlider.setAttribute('aria-valuenow', val);
      sleepSlider.setAttribute('aria-valuetext', `${h} hours ${m} minutes`);
    }

    const sleepScoreSlider = document.getElementById('perf-subjective-sleep-score');
    const sleepScoreDisplay = document.getElementById('perf-sleep-score-display');
    if (sleepScoreSlider && sleepScoreDisplay) {
      sleepScoreDisplay.innerText = `${sleepScoreSlider.value} / 100`;
      sleepScoreSlider.setAttribute('aria-valuenow', sleepScoreSlider.value);
      sleepScoreSlider.setAttribute('aria-valuetext', `${sleepScoreSlider.value} out of 100`);
    }
  }

  // Pill click & keyboard arrow listeners
  function setupPerfRating(containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;
    const pills = Array.from(container.querySelectorAll('.rating-pill-btn'));

    container.addEventListener('click', (e) => {
      const btn = e.target.closest('.rating-pill-btn');
      if (!btn) return;
      pills.forEach(b => {
        b.classList.remove('active');
        b.setAttribute('aria-checked', 'false');
        b.setAttribute('tabindex', '-1');
      });
      btn.classList.add('active');
      btn.setAttribute('aria-checked', 'true');
      btn.setAttribute('tabindex', '0');
      btn.focus();
      updatePerfSubjectiveLabels();
    });

    container.addEventListener('keydown', (e) => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        const activeIdx = pills.findIndex(b => b.classList.contains('active'));
        let nextIdx = 0;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          nextIdx = activeIdx >= 0 ? (activeIdx + 1) % pills.length : 0;
        } else {
          nextIdx = activeIdx > 0 ? activeIdx - 1 : pills.length - 1;
        }
        pills.forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-checked', 'false');
          b.setAttribute('tabindex', '-1');
        });
        const nextBtn = pills[nextIdx];
        if (nextBtn) {
          nextBtn.classList.add('active');
          nextBtn.setAttribute('aria-checked', 'true');
          nextBtn.setAttribute('tabindex', '0');
          nextBtn.focus();
          updatePerfSubjectiveLabels();
        }
      }
    });
  }

  setupPerfRating('perf-phys-rating-container');
  setupPerfRating('perf-ment-rating-container');

  // Sliders input listeners
  ['perf-subjective-resting-hr', 'perf-subjective-hrv', 'perf-subjective-sleep', 'perf-subjective-sleep-score'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('input', updatePerfSubjectiveLabels);
  });

  // Open modal handler
  if (updateStatusBtn) {
    updateStatusBtn.addEventListener('click', () => {
      // Trigger fresh HealthKit summary sync from native companion
      requestHealthKitSummary();

      dialog._focusAfterClosed = updateStatusBtn;
      const current = getDailySubjectiveRecovery() || store.todayHealth || {};

      const physContainer = document.getElementById('perf-phys-rating-container');
      if (physContainer) {
        physContainer.querySelectorAll('.rating-pill-btn').forEach(b => {
          const isSelected = current.physicalFeeling && b.getAttribute('data-value') === String(current.physicalFeeling);
          b.classList.toggle('active', !!isSelected);
          b.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          b.setAttribute('tabindex', isSelected ? '0' : '-1');
        });
      }

      const mentContainer = document.getElementById('perf-ment-rating-container');
      if (mentContainer) {
        mentContainer.querySelectorAll('.rating-pill-btn').forEach(b => {
          const isSelected = current.mentalFeeling && b.getAttribute('data-value') === String(current.mentalFeeling);
          b.classList.toggle('active', !!isSelected);
          b.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          b.setAttribute('tabindex', isSelected ? '0' : '-1');
        });
      }

      const restingHrSlider = document.getElementById('perf-subjective-resting-hr');
      const rhrVal = current.restingHr !== undefined ? current.restingHr : (current.restingHR !== undefined ? current.restingHR : 60);
      if (restingHrSlider) {
        restingHrSlider.value = rhrVal;
        restingHrSlider.setAttribute('aria-valuenow', rhrVal);
        restingHrSlider.setAttribute('aria-valuetext', `${rhrVal} beats per minute`);
      }

      const hrvSlider = document.getElementById('perf-subjective-hrv');
      const hrvVal = current.hrv !== undefined ? current.hrv : 50;
      if (hrvSlider) {
        hrvSlider.value = hrvVal;
        hrvSlider.setAttribute('aria-valuenow', hrvVal);
        hrvSlider.setAttribute('aria-valuetext', `${hrvVal} milliseconds`);
      }

      const sleepSlider = document.getElementById('perf-subjective-sleep');
      if (sleepSlider) {
        const h = current.sleepHours !== undefined ? current.sleepHours : 7;
        const m = current.sleepMinutes !== undefined ? current.sleepMinutes : 30;
        const sleepTotal = h + (m / 60);
        sleepSlider.value = sleepTotal;
        sleepSlider.setAttribute('aria-valuenow', sleepTotal);
        sleepSlider.setAttribute('aria-valuetext', `${h} hours ${m} minutes`);
      }

      const sleepScoreSlider = document.getElementById('perf-subjective-sleep-score');
      const sleepScoreVal = current.sleepScore !== undefined ? current.sleepScore : 70;
      if (sleepScoreSlider) {
        sleepScoreSlider.value = sleepScoreVal;
        sleepScoreSlider.setAttribute('aria-valuenow', sleepScoreVal);
        sleepScoreSlider.setAttribute('aria-valuetext', `${sleepScoreVal} out of 100`);
      }

      // Populate dialog status banner
      const banner = document.getElementById('perf-dialog-status-banner');
      if (banner) {
        if (current.source === 'healthkit' || (!current.source && store.todayHealth && (store.todayHealth.restingHr || store.todayHealth.sleepScore))) {
          banner.style.display = 'block';
          banner.style.background = 'rgba(16, 185, 129, 0.12)';
          banner.style.border = '1px solid rgba(16, 185, 129, 0.35)';
          banner.style.color = '#34d399';
          banner.innerText = '🍏 Telemetry pre-filled from Apple HealthKit today';
        } else if (current.source === 'hybrid') {
          banner.style.display = 'block';
          banner.style.background = 'rgba(168, 85, 247, 0.12)';
          banner.style.border = '1px solid rgba(168, 85, 247, 0.35)';
          banner.style.color = '#c084fc';
          banner.innerText = '⚡ Hybrid: Apple HealthKit biometrics + manual subjective feelings';
        } else if (current.source === 'manual') {
          banner.style.display = 'block';
          banner.style.background = 'rgba(99, 102, 241, 0.12)';
          banner.style.border = '1px solid rgba(99, 102, 241, 0.35)';
          banner.style.color = '#a5b4fc';
          banner.innerText = '✏️ Loaded existing manual check-in for today';
        } else {
          banner.style.display = 'block';
          banner.style.background = 'rgba(255, 255, 255, 0.05)';
          banner.style.border = '1px solid var(--border-light)';
          banner.style.color = 'var(--text-muted)';
          banner.innerText = 'ℹ️ Adjust sliders and ratings below to log your daily recovery.';
        }
      }

      updatePerfSubjectiveLabels();

      dialog.style.display = 'flex';
      if (typeof dialog.showModal === 'function') dialog.showModal();
    });
  }

  // Save button handler with mutex
  if (saveBtn) {
    saveBtn.addEventListener('click', () => {
      if (isSavingRecovery) return;
      isSavingRecovery = true;
      saveBtn.disabled = true;
      const originalSaveHtml = saveBtn.innerHTML;
      saveBtn.innerHTML = '<span>⚡ Saving...</span>';

      try {
        const physContainer = document.getElementById('perf-phys-rating-container');
        const activePhysBtn = physContainer ? physContainer.querySelector('.rating-pill-btn.active') : null;
        const physVal = activePhysBtn ? parseInt(activePhysBtn.getAttribute('data-value'), 10) : null;

        const mentContainer = document.getElementById('perf-ment-rating-container');
        const activeMentBtn = mentContainer ? mentContainer.querySelector('.rating-pill-btn.active') : null;
        const mentVal = activeMentBtn ? parseInt(activeMentBtn.getAttribute('data-value'), 10) : null;

        const restingHrSlider = document.getElementById('perf-subjective-resting-hr');
        const restHrVal = restingHrSlider ? parseInt(restingHrSlider.value, 10) : 60;

        const hrvSlider = document.getElementById('perf-subjective-hrv');
        const hrvVal = hrvSlider ? parseInt(hrvSlider.value, 10) : 50;

        const sleepScoreSlider = document.getElementById('perf-subjective-sleep-score');
        const sleepScoreVal = sleepScoreSlider ? parseInt(sleepScoreSlider.value, 10) : 70;

        const sleepSlider = document.getElementById('perf-subjective-sleep');
        let sleepHours = 7;
        let sleepMinutes = 30;
        if (sleepSlider) {
          const val = parseFloat(sleepSlider.value);
          sleepHours = Math.floor(val);
          sleepMinutes = Math.round((val - sleepHours) * 60);
        }

        let source = 'manual';
        if (store.todayHealth) {
          source = (physVal || mentVal) ? 'hybrid' : 'healthkit';
        }

        const updatedRecovery = {
          physicalFeeling: physVal,
          mentalFeeling: mentVal,
          restingHr: restHrVal,
          hrv: hrvVal,
          sleepHours: sleepHours,
          sleepMinutes: sleepMinutes,
          sleepScore: sleepScoreVal,
          source: source
        };

        const metrics = calculatePerformanceMetrics();
        const tsbNorm = Math.min(100, Math.max(0, ((metrics.form + 25) / 50) * 100));
        const acuteRec = calculateAcuteRecoveryScore(updatedRecovery);
        updatedRecovery.readinessScore = Math.round(0.4 * tsbNorm + 0.6 * acuteRec);

        saveDailySubjectiveRecovery(updatedRecovery);
        renderPerformanceView();
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('Daily recovery check-in saved successfully!', 'success');
        }
        closeModal();
      } catch (err) {
        console.warn('Error saving recovery check-in:', err);
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('Daily recovery check-in saved locally.', 'info');
        }
        closeModal();
      } finally {
        isSavingRecovery = false;
        saveBtn.disabled = false;
        saveBtn.innerHTML = originalSaveHtml;
      }
    });
  }
}

let isPerformanceInitialized = false;

export function initPerformanceView() {
  if (isPerformanceInitialized) return;
  isPerformanceInitialized = true;

  // Request fresh HealthKit summary on view mount
  requestHealthKitSummary();

  const debugActiveChk = document.getElementById('chk-debug-active');
  const debugInjectBtn = document.getElementById('btn-debug-inject');
  const debugRestoreBtn = document.getElementById('btn-debug-restore');
  
  if (debugActiveChk) {
    debugActiveChk.addEventListener('change', () => {
      const panel = document.getElementById('debug-controls-content');
      if (panel) {
        panel.style.display = debugActiveChk.checked ? 'flex' : 'none';
      }
    });
  }
  
  if (debugInjectBtn) {
    debugInjectBtn.addEventListener('click', () => {
      const scenarioSelect = document.getElementById('sel-debug-scenario');
      const scenario = scenarioSelect ? scenarioSelect.value : 'overtraining';
      injectMockScenario(scenario);
    });
  }
  
  if (debugRestoreBtn) {
    debugRestoreBtn.addEventListener('click', () => {
      restoreOriginalWorkouts();
    });
  }

  setupPerformanceStatusModal();

  const banisterInfoBtn = document.getElementById('btn-banister-info');
  if (banisterInfoBtn) {
    banisterInfoBtn.addEventListener('click', () => {
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast("💡 Banister Model: CTL (Fitness) = 42-day rolling load; ATL (Fatigue) = 7-day load; Form (TSB) = Fitness - Fatigue.", "info");
      }
    });
  }
}
